import { compressForExtraction } from './compressImage.js'

/**
 * Turning the chosen images into one document.
 *
 * Two stages, and only the first is per image: every photograph is compressed
 * in the browser, one after another, and then all of them go to the API in a
 * single request that comes back as one document.
 *
 * ── Why one request ──────────────────────────────────────────────────────
 *
 * It used to be one request per page, which made a single bad photograph
 * recoverable on its own. The trade has been made deliberately the other way:
 * ten pages are now one round trip rather than ten sequential ones, and the
 * reader sees the pages as the single document they are — a sentence carried
 * across a page break is transcribed as that sentence instead of as two
 * fragments nothing can rejoin.
 *
 * What that costs is per-page retry. A failure is now the whole reading, and
 * the way out is to read the pages again or to remove the photograph that came
 * out badly. What it does *not* cost is the guarantee that every page arrived:
 * the server checks that as many sections came back as images went out, and
 * refuses a short answer rather than returning a document with a page missing.
 *
 * Compression stays sequential. It is the browser's own CPU rather than the
 * network, so running ten at once would contend for the same main thread and
 * arrive no sooner — and doing them in turn is what lets the tiles say which
 * page is being prepared.
 */

/** Where one image has got to. */
export type PageStatus = 'pending' | 'compressing' | 'extracting' | 'extracted' | 'failed'

export interface PageState {
  /** The selection's id for the image, so a result cannot land on the wrong tile. */
  id: string
  status: PageStatus
  /** The prepared bytes, once the browser has them. Cleared once sent. */
  base64: string | null
  /**
   * What the page weighed after compression, in bytes. Null until it has been
   * prepared, and kept afterwards — it is a number, not a payload, and the
   * size report reads it once the base64 has been let go.
   */
  compressedBytes: number | null
  /** A `userError` code, or a local reason, once it has failed. */
  errorCode: string | null
}

/** What the caller must supply: sending the whole batch, however it likes. */
export interface SendPages {
  (
    images: readonly { base64: string; mimeType: 'image/jpeg' }[],
  ): Promise<{ ok: true; markdown: string } | { ok: false; code: string }>
}

export interface ExtractPagesOptions {
  send: SendPages
  /** Called after every status change, with the whole list. */
  onProgress?: (pages: readonly PageState[]) => void
  /** Swapped in tests. Defaults to the real browser compression. */
  compress?: typeof compressForExtraction
}

/** A local code for a file the browser could not prepare at all. */
export const COMPRESSION_FAILED = 'IMAGE_COMPRESSION_FAILED'

/** What a reading produced, or the code that says why it did not. */
export type ExtractionResult =
  | { ok: true; pages: PageState[]; markdown: string }
  | { ok: false; pages: PageState[]; code: string }

export function initialPages(ids: readonly string[]): PageState[] {
  return ids.map((id) => ({
    id,
    status: 'pending',
    base64: null,
    compressedBytes: null,
    errorCode: null,
  }))
}

/**
 * Prepare every page, then read them all in one request.
 *
 * A photograph the browser cannot even decode is the one failure that is still
 * per page: it never reaches the network, so it is marked on its tile and the
 * reading is abandoned rather than sending a batch the tutor did not choose.
 * Reading nine of ten pages and saying nothing is exactly the silent gap this
 * whole flow is built to avoid.
 */
export async function extractPages(
  images: readonly { id: string; file: File }[],
  pages: readonly PageState[],
  { send, onProgress, compress = compressForExtraction }: ExtractPagesOptions,
): Promise<ExtractionResult> {
  const next = pages.map((page) => ({ ...page }))
  const report = () => onProgress?.(next.map((page) => ({ ...page })))

  const set = (index: number, patch: Partial<PageState>) => {
    const current = next[index]
    if (!current) return
    next[index] = { ...current, ...patch }
    report()
  }

  for (const [index, image] of images.entries()) {
    set(index, { status: 'compressing', errorCode: null, base64: null, compressedBytes: null })

    try {
      const prepared = await compress(image.file)
      set(index, { base64: prepared.base64, compressedBytes: prepared.bytes })
    } catch {
      set(index, { status: 'failed', errorCode: COMPRESSION_FAILED })
      // Every other page reverts to waiting: nothing was sent, so nothing was
      // read, and a tile still reading would be describing a request that
      // never happened.
      for (const [other] of images.entries()) {
        if (other !== index && next[other]?.status !== 'failed') {
          set(other, { status: 'pending', base64: null })
        }
      }
      return { ok: false, pages: next, code: COMPRESSION_FAILED }
    }
  }

  for (const [index] of images.entries()) set(index, { status: 'extracting' })

  // Selection order, which is the only thing that says which page is which.
  const result = await send(
    next.map((page) => ({ base64: page.base64 ?? '', mimeType: 'image/jpeg' as const })),
  )

  if (!result.ok) {
    for (const [index] of images.entries()) {
      set(index, { status: 'failed', errorCode: result.code, base64: null })
    }
    return { ok: false, pages: next, code: result.code }
  }

  // All of them or none: the server refused a short answer, so a success here
  // is a document covering every page.
  for (const [index] of images.entries()) {
    set(index, { status: 'extracted', errorCode: null, base64: null })
  }
  return { ok: true, pages: next, markdown: result.markdown }
}

/** Whether every page was read. A reading is all of them or none. */
export function allExtracted(pages: readonly PageState[]): boolean {
  return pages.length > 0 && pages.every((page) => page.status === 'extracted')
}

export function failedCount(pages: readonly PageState[]): number {
  return pages.filter((page) => page.status === 'failed').length
}
